import apiClient from './apiClient';

// Where Channex should call this backend back: the public API origin, https,
// no port, no trailing slash. Dev admine talks to devapi; otherwise the
// configured API URL minus its /api suffix, falling back to production.
export const defaultCallbackBaseUrl = () => {
  const fromEnv = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const base = host.includes('dev')
    ? 'https://devapi.staydesko.com'
    : (fromEnv || 'https://api.staydesko.com');
  return base.replace(/^http:\/\//, 'https://').replace(/\/+$/, '');
};

// Channex channel-manager operator API.
// Every endpoint is superuser-only, enforced server-side by IsSuperUser —
// hiding the nav entry is cosmetic, not access control.
// Note admine's apiClient already carries `/api` in its base URL.
const channexService = {
  list: () => apiClient.get('/channel-manager/channex/configs/'),
  get: (id) => apiClient.get(`/channel-manager/channex/configs/${id}/`),
  update: (id, data) => apiClient.patch(`/channel-manager/channex/configs/${id}/`, data),
  getMappings: (id) => apiClient.get(`/channel-manager/channex/configs/${id}/mappings/`),
  // Edit one rate mapping row (is_active / is_primary / rate adjustment).
  updateRateMapping: (id, body) =>
    apiClient.patch(`/channel-manager/channex/configs/${id}/mappings/`, body),
  // Create the per-guest-count rate mappings (Standard 1A/2A/3A, Channex
  // codes pending) for review BEFORE the catalogue sync. enable also
  // switches the hotel to occupancy-based (per_person) plans.
  prepareMappings: (id, { dryRun = false, enable = false } = {}) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/prepare-mappings/`,
      { dry_run: dryRun, enable }),

  testConnection: (id) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/test-connection/`, {}),
  // dry_run validates and returns the payload WITHOUT creating a billable
  // Channex property ($7/property/month).
  provision: (id, dryRun = false) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/provision/`, { dry_run: dryRun }),
  syncCatalog: (id, dryRun = false) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/sync-catalog/`, { dry_run: dryRun }),
  fullSync: (id, days) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/full-sync/`, { days }),
  pollBookings: (id) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/poll-bookings/`, {}),
  // OTA guest chat: installs the Channex Messages app on the property,
  // registers the messaging webhook and pulls existing threads.
  // The base URL is sent explicitly (same default as the onboarding form):
  // left blank, the backend had to infer it from the request, which behind
  // Railway's TLS edge came out as http:// and Channex refused the webhook
  // with "invalid port, 443 is expected".
  enableMessaging: (id) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/messaging/`, {
      callback_base_url: defaultCallbackBaseUrl(),
    }),
  disableMessaging: (id) =>
    apiClient.del(`/channel-manager/channex/configs/${id}/messaging/`),
  // Card guarantee: installs the Channex Payment Application on the
  // property. Connecting Stripe stays the hotel owner's click in
  // Booking Engine · Payments — the account is theirs, not ours.
  enableCardPayments: (id) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/card-payments/`, {}),
  disableCardPayments: (id) =>
    apiClient.del(`/channel-manager/channex/configs/${id}/card-payments/`),
  // Latest bookings for the property, read from OUR feed log (no Channex
  // call), each paired with the FD reservation it became.
  loggedBookings: (id, limit = 50) =>
    apiClient.get(`/channel-manager/channex/configs/${id}/logged-bookings/?limit=${limit}`),
  // Re-run one logged booking through the current parser + billing.
  // mode 'replay' updates the reservation in place; 'recreate' deletes it
  // and imports fresh (refused server-side when staff work hangs off it).
  // roomTypeId (FD RoomType id) is required when Channex sent the booking
  // without a room type (OTA room unmapped on the Channex side);
  // roomNumber puts the booking in that physical room.
  replayBooking: (id, otaCode, { dryRun = true, mode = 'replay', roomTypeId = null, roomNumber = '' } = {}) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/replay-booking/`, {
      ota_code: otaCode,
      dry_run: dryRun,
      mode,
      ...(roomTypeId ? { room_type_id: roomTypeId } : {}),
      ...(roomNumber ? { room_number: roomNumber } : {}),
    }),

  clearQuarantine: (id) =>
    apiClient.patch(`/channel-manager/channex/configs/${id}/`, { clear_quarantine: true }),

  // --- onboarding (P5) ---
  // Hotels that could be put onto Channex, plus which already are and which
  // run another channel manager.
  onboardableHotels: () => apiClient.get('/channel-manager/channex/onboardable-hotels/'),
  // One call: config -> property -> catalogue -> webhook. Idempotent.
  // Credentials are cloned server-side from an existing config, never sent.
  onboard: (body) => apiClient.post('/channel-manager/channex/onboard/', body),

  // --- channel connect (P5) ---
  availableChannels: (id) =>
    apiClient.get(`/channel-manager/channex/configs/${id}/available-channels/`),
  // Each OTA needs different settings, so the form is rendered from this.
  channelAdapter: (id, code) =>
    apiClient.get(`/channel-manager/channex/configs/${id}/channel-adapter/${code}/`),
  connections: (id) => apiClient.get(`/channel-manager/channex/configs/${id}/channels/`),
  testChannel: (id, code, settings) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/channels/test/`, { code, settings }),
  mappingOptions: (id, code, settings) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/channels/mapping-options/`, { code, settings }),
  createChannel: (id, body) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/channels/`, body),
  // Separate from create on purpose: activating makes inventory publicly
  // bookable on the OTA.
  channelAction: (id, channelId, action) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/channels/${channelId}/${action}/`, {}),

  // Embed URL for Channex's own channel-mapping screen (one-time token,
  // minted server-side; single use — request a fresh one per open).
  mappingScreen: (id) =>
    apiClient.post(`/channel-manager/channex/configs/${id}/mapping-screen/`, {}),
};

export default channexService;
